import {
  type APIUser,
  ChannelType,
  fileUrl,
  selectBadgeCount,
  THREAD_CHANNEL_TYPES,
} from '@alternativamais/chat-core';
import { type ReactNode, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '../../../componentes/Avatar';
import { Flutuante, type Posicao } from '../../../componentes/Flutuante';
import { Icone, type NomeIcone } from '../../../componentes/Icone';
import { Markdown } from '../../../componentes/Markdown';
import { type PedidoTela, useAtender } from '../../../chat/atalhos';
import { abrirCamada } from '../../../chat/camadas';
import { useEstado } from '../../../chat/contexto';
import { useNome } from '../../../chat/ganchos';
import { ARQUIVOS } from '../../../config';
import { iconeDoCanal } from '../../../utilidades/canais';
import { CampoBusca } from '../busca/CampoBusca';
import { CaixaDeEntrada } from '../caixa/CaixaDeEntrada';
import { NovoGrupo } from '../dms/NovoGrupo';
import { Fixadas } from '../fixadas/Fixadas';
import { ListaThreads } from '../threads/ListaThreads';
import { MenuNotificacoes } from './MenuNotificacoes';
import { BotaoGaveta, useJanelaEstreita } from '../../../chat/gaveta';

/**
 * Cabeçalho da conversa (48 px): ícone e nome (thread mostra o canal pai;
 * DM, a pessoa; grupo, os membros), o tópico e as ferramentas do design.
 */
export function Cabecalho({
  canalId,
  lateralAberta,
  aoAlternarLateral,
  textoDaBusca,
  aoBuscar,
  aoCriarThread,
}: {
  canalId: string;
  lateralAberta: boolean;
  aoAlternarLateral: () => void;
  textoDaBusca: string;
  aoBuscar: (texto: string) => void;
  aoCriarThread?: () => void;
}) {
  const canal = useEstado((estado) => estado.channels[canalId]);
  const estreita = useJanelaEstreita();
  if (!canal) return null;
  const thread = THREAD_CHANNEL_TYPES.has(canal.type);
  const dm = canal.type === ChannelType.DM;
  const grupo = canal.type === ChannelType.GROUP_DM;
  const servidor = !!canal.guild_id;
  // Fórum: sem threads, fixadas, membros e busca de mensagens (a lista dele busca).
  const forum =
    canal.type === ChannelType.GUILD_FORUM ||
    canal.type === ChannelType.GUILD_MEDIA;
  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b border-divisor pr-3 pl-4 max-md:gap-2 max-md:pr-1 max-md:pl-3">
      <BotaoGaveta />
      {dm && canal.recipients?.[0] ? (
        <TituloDm pessoa={canal.recipients[0]} />
      ) : grupo ? (
        <TituloGrupo canalId={canalId} />
      ) : thread ? (
        <TituloThread canalId={canalId} />
      ) : (
        <>
          <Icone
            nome={iconeDoCanal(canal)}
            tamanho={22}
            className="shrink-0 text-texto-3"
          />
          <h1 className="m-0 min-w-0 truncate text-base font-semibold whitespace-nowrap md:max-w-[60%] md:shrink-0">
            {canal.name}
          </h1>
        </>
      )}
      {canal.topic && servidor && !thread && !estreita ? (
        <>
          <span className="h-6 w-px shrink-0 bg-borda" />
          <div
            className="min-w-0 grow overflow-hidden text-sm text-texto-3"
            title={canal.topic}
          >
            <Markdown
              texto={canal.topic}
              guildId={canal.guild_id ?? null}
              umaLinha
            />
          </div>
        </>
      ) : (
        <span className="grow" />
      )}
      <nav
        aria-label="Ferramentas da conversa"
        className="flex shrink-0 items-center gap-1 max-md:gap-0"
      >
        {servidor && !thread && !forum && (
          <Ferramenta rotulo="Threads" icone="thread">
            {(fechar) => (
              <ListaThreads
                canalId={canalId}
                aoFechar={fechar}
                aoCriar={
                  aoCriarThread
                    ? () => {
                        fechar();
                        aoCriarThread();
                      }
                    : undefined
                }
              />
            )}
          </Ferramenta>
        )}
        {servidor && (
          <Ferramenta rotulo="Notificações do canal" icone="sino">
            {(fechar) => (
              <MenuNotificacoes canalId={canalId} aoFechar={fechar} />
            )}
          </Ferramenta>
        )}
        {!forum && (
          <Ferramenta rotulo="Mensagens fixadas" icone="pin" pedido="fixadas">
            {(fechar) => <Fixadas canalId={canalId} aoFechar={fechar} />}
          </Ferramenta>
        )}
        {(dm || grupo) && (
          <Ferramenta rotulo="Adicionar pessoas ao grupo" icone="usuario-mais">
            {(fechar) => <NovoGrupo canalId={canalId} aoFechar={fechar} />}
          </Ferramenta>
        )}
        {(servidor || grupo || dm) && !forum && (
          <AtalhoMembros aoAlternar={aoAlternarLateral} />
        )}
        {(servidor || grupo || dm) && !forum && !estreita && (
          <button
            type="button"
            aria-label={
              dm
                ? lateralAberta
                  ? 'Esconder perfil'
                  : 'Mostrar perfil'
                : lateralAberta
                  ? 'Esconder lista de membros'
                  : 'Mostrar lista de membros'
            }
            title={dm ? 'Perfil' : 'Membros'}
            aria-pressed={lateralAberta}
            onClick={aoAlternarLateral}
            className={`flex size-9 items-center justify-center rounded-lg hover:text-texto ${
              lateralAberta ? 'text-texto' : 'text-texto-2'
            }`}
          >
            <Icone nome={dm ? 'usuario' : 'pessoas'} tamanho={22} />
          </button>
        )}
        {!forum && (
          <CampoBusca
            canalId={canalId}
            valor={textoDaBusca}
            aoBuscar={aoBuscar}
          />
        )}
        <BotaoCaixa />
      </nav>
    </header>
  );
}

/** Ctrl+U mostra ou esconde a lista de membros. */
function AtalhoMembros({ aoAlternar }: { aoAlternar: () => void }) {
  useAtender('membros', aoAlternar);
  return null;
}

/** Botão de ferramenta com o seu popover (abre abaixo, alinhado à direita). */
export function Ferramenta({
  rotulo,
  icone,
  children,
  marcado = false,
  pedido,
}: {
  rotulo: string;
  icone: NomeIcone;
  children: (fechar: () => void) => ReactNode;
  marcado?: boolean;
  /** Atalho de teclado que abre (Ctrl+P, Ctrl+I). */
  pedido?: PedidoTela;
}) {
  const [ancora, setAncora] = useState<Posicao | null>(null);
  const botao = useRef<HTMLButtonElement>(null);
  useAtender(
    pedido ?? 'caixa',
    () => {
      const caixa = botao.current?.getBoundingClientRect();
      if (!caixa || !botao.current?.offsetParent) return;
      setAncora((atual) =>
        atual ? null : { x: caixa.right, y: caixa.bottom + 6 },
      );
    },
    !!pedido,
  );
  return (
    <>
      <button
        ref={botao}
        type="button"
        aria-label={rotulo}
        title={rotulo}
        aria-haspopup="dialog"
        aria-expanded={!!ancora}
        onClick={(evento) => {
          const caixa = evento.currentTarget.getBoundingClientRect();
          setAncora(ancora ? null : { x: caixa.right, y: caixa.bottom + 6 });
        }}
        className={`relative flex size-9 items-center justify-center rounded-lg hover:text-texto ${
          ancora ? 'text-texto' : 'text-texto-2'
        }`}
      >
        <Icone nome={icone} tamanho={22} />
        {marcado && (
          <span className="absolute top-[5px] right-[5px] size-2 rounded-full border-2 border-chat bg-perigo box-content" />
        )}
      </button>
      {ancora && (
        <Flutuante
          ancora={ancora}
          aoFechar={() => setAncora(null)}
          rotulo={rotulo}
          alinhar="fim"
        >
          {children(() => setAncora(null))}
        </Flutuante>
      )}
    </>
  );
}

export function BotaoCaixa() {
  const novas = useEstado(selectBadgeCount);
  return (
    <Ferramenta
      rotulo={novas ? `Caixa de entrada, ${novas} novas` : 'Caixa de entrada'}
      icone="caixa-entrada"
      marcado={novas > 0}
      pedido="caixa"
    >
      {(fechar) => <CaixaDeEntrada aoFechar={fechar} />}
    </Ferramenta>
  );
}

function TituloDm({ pessoa }: { pessoa: APIUser }) {
  const nome = useNome(pessoa.id, null, pessoa);
  const status = useEstado(
    (estado) => estado.presences[pessoa.id]?.status ?? 'offline',
  );
  return (
    <button
      type="button"
      onClick={(evento) => {
        const caixa = evento.currentTarget.getBoundingClientRect();
        abrirCamada({
          tipo: 'perfil',
          userId: pessoa.id,
          guildId: null,
          ancora: { x: caixa.left, y: caixa.bottom + 6 },
        });
      }}
      className="flex min-w-0 shrink-0 items-center gap-2.5 rounded-md hover:text-texto"
    >
      <Avatar pessoa={pessoa} nome={nome} tamanho={24} status={status} />
      <h1 className="m-0 truncate text-base font-semibold">{nome}</h1>
    </button>
  );
}

function TituloGrupo({ canalId }: { canalId: string }) {
  const canal = useEstado((estado) => estado.channels[canalId]);
  const membros = canal?.recipients ?? [];
  const nome =
    canal?.name ||
    membros.map((pessoa) => pessoa.global_name ?? pessoa.username).join(', ');
  return (
    <button
      type="button"
      title="Editar grupo"
      onClick={() => abrirCamada({ tipo: 'config-grupo', canalId })}
      className="flex min-w-0 items-center gap-2.5 rounded-md text-left"
    >
      {canal && <IconeDoGrupo canal={canal} />}
      <h1 className="m-0 truncate text-base font-semibold">{nome}</h1>
      <span className="shrink-0 text-sm text-texto-3">
        {membros.length + 1} membros
      </span>
    </button>
  );
}

/** Ícone do grupo (o próprio, ou os membros empilhados). */
export function IconeDoGrupo({
  canal,
  tamanho = 24,
}: {
  canal: { id: string; icon?: string | null; recipients?: APIUser[] };
  tamanho?: number;
}) {
  if (canal.icon) {
    return (
      <img
        src={fileUrl(ARQUIVOS, `channel-icons/${canal.id}/${canal.icon}.png`)}
        alt=""
        className="shrink-0 rounded-full object-cover"
        style={{ width: tamanho, height: tamanho }}
      />
    );
  }
  return (
    <AvataresEmpilhados pessoas={canal.recipients ?? []} tamanho={tamanho} />
  );
}

export function AvataresEmpilhados({
  pessoas,
  tamanho = 24,
}: {
  pessoas: APIUser[];
  tamanho?: number;
}) {
  const [a, b] = pessoas;
  if (!a) {
    return (
      <span
        className="flex shrink-0 items-center justify-center rounded-full bg-ativo"
        style={{ width: tamanho, height: tamanho }}
      >
        <Icone nome="pessoas" tamanho={tamanho * 0.6} />
      </span>
    );
  }
  if (!b)
    return (
      <Avatar pessoa={a} nome={a.global_name ?? a.username} tamanho={tamanho} />
    );
  const menor = Math.round(tamanho * 0.72);
  return (
    <span
      className="relative shrink-0"
      style={{ width: tamanho, height: tamanho }}
    >
      <span className="absolute top-0 left-0">
        <Avatar pessoa={a} nome={a.global_name ?? a.username} tamanho={menor} />
      </span>
      <span className="absolute right-0 bottom-0 rounded-full ring-2 ring-chat">
        <Avatar pessoa={b} nome={b.global_name ?? b.username} tamanho={menor} />
      </span>
    </span>
  );
}

function TituloThread({ canalId }: { canalId: string }) {
  const navegar = useNavigate();
  const thread = useEstado((estado) => estado.channels[canalId]);
  const pai = useEstado((estado) =>
    thread?.parent_id ? estado.channels[thread.parent_id] : undefined,
  );
  if (!thread) return null;
  return (
    <div className="flex min-w-0 shrink items-center gap-1.5">
      {pai && (
        <>
          <button
            type="button"
            onClick={() => navegar(`/canais/${pai.guild_id}/${pai.id}`)}
            className="flex min-w-0 items-center gap-1.5 text-texto-3 hover:text-texto"
          >
            <Icone nome={iconeDoCanal(pai)} tamanho={20} className="shrink-0" />
            <span className="truncate text-base font-semibold">{pai.name}</span>
          </button>
          <Icone
            nome="chevron-direita"
            tamanho={16}
            className="shrink-0 text-texto-3"
          />
        </>
      )}
      <Icone nome="thread" tamanho={20} className="shrink-0 text-texto-3" />
      <h1 className="m-0 truncate text-base font-semibold">{thread.name}</h1>
    </div>
  );
}
